import React, { useEffect, useState } from 'react';
import {
  Button,
  ButtonVariant,
  IconSize,
  IconWrapper,
  Link,
  Loader,
  LoaderSize,
} from 'components/common';
import { useNavigate, useParams } from 'react-router-dom';
import { Role, useGetUserById, useSyncBotInfo, GET_USER_BY_ID_KEY } from 'apis/users';
import { InitUser, RoleUser } from 'types/users';
import { ReactComponent as ChevronLeftIcon } from 'assets/icons/chevron-left.svg';
import EditUserForm from './EditUserForm';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { centToStringDollar } from 'helpers';
import moment from 'moment';
import { ReactComponent as ExternalLink } from 'assets/icons/external-link.svg';
import { ReactComponent as Cog } from 'assets/icons/cog.svg';
import { ReactComponent as RefreshIcon } from 'assets/icons/refresh.svg';
import EditSubscriptionModal from './EditSubscriptionModal';
import BotDetailsModal from './BotDetailsModal';
import { toast } from 'react-toastify';
import { useQueryClient } from 'react-query';

const UserEdit = () => {
  const [initUser, setInitUser] = useState<InitUser>(null);
  const [localUser, setLocalUser] = useState<any>(null);
  const navigate = useNavigate();
  const params = useParams();
  const queryClient = useQueryClient();
  const { data: user, isLoading, isFetching } = useGetUserById(params?.id);
  const [open, setOpen] = useState(false);
  const [botDetailsModalOpen, setBotDetailsModalOpen] = useState(false);
  const { mutate: syncBotInfo, isLoading: isSyncing } = useSyncBotInfo();

  useEffect(() => {
    if (user) {
      setLocalUser(user);
      setInitUser({
        email: user?.email,
        name: user?.user_metadata?.fullName || '',
        enableLogoCustomize: user?.app_metadata?.enableLogoCustomize,
        enableLeadGenTool: user?.app_metadata?.enableLeadGenTool,
        role:
          user?.app_metadata?.role === Role.RAMMP_ADMIN
            ? RoleUser.ADMIN
            : RoleUser.USER,
        reportCredits: user?.app_metadata?.report_credit || 0,
      });
    }
  }, [user]);

  const handleSyncBotInfo = () => {
    if (!params?.id) return;

    syncBotInfo(params.id, {
      onSuccess: (response) => {
        if (response?.data) {
          // Update local state with the synced data
          setLocalUser((prevUser: any) => ({
            ...prevUser,
            botInfo: response.data.synced,
            hubspot: response.data.hubspot, // Complete hubspot object from API
            sync_analysis: response.data.sync_analysis
          }));

          // Use the message from backend for all toasts
          const message = response.data.message || 'Sync completed';

          switch (response.data.sync_status) {
            case 'synced':
              toast.success(message);
              break;
            case 'already_synced':
              toast.info(message);
              break;
            case 'partial_sync':
              toast.warning(message);
              // Show issues if present
              if (response.data.sync_analysis?.issues?.length > 0) {
                const issueCount = response.data.sync_analysis.issues.length;
                toast.warning(`${issueCount} bot(s) have incomplete data`, { autoClose: 5000 });
              }
              break;
            case 'no_hubspot_data':
              toast.error(message);
              break;
            default:
              toast.warning(message);
          }
        }
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to sync bot info');
      }
    });
  };

  const handleRefreshHubSpotData = async () => {
    if (!params?.id) return;

    await queryClient.invalidateQueries([GET_USER_BY_ID_KEY, params.id]);
    toast.info('HubSpot data refreshed');
  };

  return (
    <div className="w-full relative p-6">
      <h1 className="mb-4 pb-4 border-b-2">Edit user: {params?.id}</h1>
      <Button
        variant={ButtonVariant.text}
        onClick={() => navigate('/users')}
        prefixIcon={ChevronLeftIcon}
        className="mb-4"
      >
        Back
      </Button>
      {isLoading || isFetching || !initUser ? (
        <div className="w-full flex items-center justify-center h-80">
          <Loader size={LoaderSize.xxl} />
        </div>
      ) : (
        <div className="flex flex-wrap gap-4">
          <EditUserForm
            userData={initUser}
            user_id={params?.id}
            subscription={localUser?.subscription || user?.subscription}
            widget={localUser?.widget || user?.widget}
          />
          <div className="w-96 bg-white rounded-lg shadow p-6">
            <h3 className="mb-4">Brands ({(localUser || user)?.brands?.length})</h3>
            <PerfectScrollbar className={'h-[calc(100%-20px)] max-h-[600px]'}>
              {(localUser || user)?.brands?.map((brand) => (
                <div
                  key={brand.id + '1'}
                  className="py-2 border-b border-surface-div"
                >
                  <Link
                    asRouterLink
                    routerLinkProps={{ to: `/brands/${brand.id}` }}
                  >
                    {brand.url}
                  </Link>
                </div>
              ))}
            </PerfectScrollbar>
          </div>
          <div className="w-96 bg-white rounded-lg shadow p-6 ">
            <h3 className="mb-4">Subscription</h3>
            {(localUser || user)?.subscription ? (
              <div>
                <div className="font-medium mb-1">Plan:</div>
                <h3 className="text-accent-main">
                  {(localUser || user)?.subscription?.plan?.name}
                </h3>
                <div className="font-medium mb-1 mt-2">Price:</div>
                <h3 className="text-accent-main">
                  ${centToStringDollar((localUser || user)?.subscription?.subscription.price)}{' '}
                  / {(localUser || user)?.subscription?.subscription.interval}
                </h3>
                <div className="font-medium mb-1 mt-2">Next billing date:</div>
                <h3 className="text-accent-main">
                  {moment(
                    (localUser || user)?.subscription?.subscription?.current_period_end,
                  ).format('LLL')}
                </h3>
                <div className="font-medium mb-1 mt-2">Payment method:</div>
                <div className="text-accent-main">
                  {(localUser || user)?.subscription?.subscription?.payment_method?.card ? (
                    <>
                      <h3 className="text-accent-main">
                        •••• •••• ••••{' '}
                        {
                          (localUser || user)?.subscription?.subscription?.payment_method?.card
                            ?.last4
                        }
                        {'  '}
                        <span className="text-content-secondary font-normal text-base">
                          Expiry{' '}
                          {
                            (localUser || user)?.subscription?.subscription?.payment_method
                              ?.card?.exp_month
                          }
                          /
                          {
                            (localUser || user)?.subscription?.subscription?.payment_method
                              ?.card?.exp_year
                          }
                        </span>
                      </h3>
                    </>
                  ) : (
                    "User doesn't have any payment method set up"
                  )}
                </div>
                <a
                  href={`https://dashboard.stripe.com/subscriptions/${(localUser || user)?.subscription?.subscription?.stripe_subscription_id}`}
                  target="__blank"
                  rel="noreferrer"
                >
                  <Button variant={ButtonVariant.outline} className="mt-2">
                    View on Stripe
                    <IconWrapper
                      className="inline-block ml-2"
                      icon={ExternalLink}
                      size={IconSize.lg}
                    />
                  </Button>
                </a>
              </div>
            ) : (
              <div>User doesn't have any subscription</div>
            )}
            <Button
              variant={ButtonVariant.outline}
              className="mt-2"
              onClick={() => setOpen(true)}
            >
              <IconWrapper
                className="inline-block mr-2"
                icon={Cog}
                size={IconSize.md}
              />
              Edit subscription
            </Button>
          </div>
          <div className="w-96 bg-white rounded-lg shadow p-6">
            <h3 className="mb-4">Bots / HubSpot</h3>

            {/* HubSpot Deal - Always at top */}
            <div className="mb-4">
              <div className="font-medium mb-1">HubSpot Deal:</div>
              {(localUser || user)?.hubspot?.deal_id ? (
                <a
                  href={`https://app-ap1.hubspot.com/contacts/23988247/record/0-3/${(localUser || user).hubspot.deal_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent-main hover:underline"
                >
                  {(localUser || user).hubspot.deal_id}
                  <IconWrapper
                    className="inline-block ml-1"
                    icon={ExternalLink}
                    size={IconSize.sm}
                  />
                </a>
              ) : (
                <span className="text-content-secondary">No deal linked</span>
              )}
            </div>

            {(() => {
              const currentUser = localUser || user;
              const dbBots = currentUser?.botInfo?.bots || [];
              const hsBots = currentUser?.hubspot?.bot_info?.bots || [];
              const syncAnalysis = currentUser?.sync_analysis;

              // NOTE: Single bot inline view is disabled - we always use the colored comparison view
              // for consistency. To re-enable, uncomment the showInline conditional below.
              // const showInline = dbBots.length <= 1 && hsBots.length <= 1;
              const showInline = false; // Always use colored comparison view

              if (showInline) {
                // UNUSED: Single Bot Inline View (kept for reference, re-enable by setting showInline above)
                // Single Bot Inline View
                const dbBot = dbBots[0];
                const hsBot = hsBots[0];

                return (
                  <>
                    {/* Database Bot Info */}
                    <div className="mb-4">
                      <div className="font-medium mb-1">Database Bot (Slot 1):</div>
                      <div className="text-content-secondary">
                        {dbBot ? (
                          <>
                            <div className="mb-1">
                              • <span className="text-accent-main">{dbBot.name}</span>
                            </div>
                            <div className="break-words">
                              • <a
                                  href={dbBot.link}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-accent-main hover:underline"
                                >
                                  {dbBot.link}
                                </a>
                            </div>
                          </>
                        ) : (
                          <span className="text-gray-400">Not set</span>
                        )}
                      </div>
                    </div>

                    {/* HubSpot Bot Info */}
                    <div className="mb-4">
                      <div className="font-medium mb-1">HubSpot Bot (Slot 1):</div>
                      <div className="text-content-secondary">
                        {hsBot && (hsBot.name || hsBot.link) ? (
                          <>
                            <div className="mb-1">
                              • <span className="text-accent-main">{hsBot.name || <span className="text-red-500">Missing name</span>}</span>
                            </div>
                            <div className="break-words">
                              • {hsBot.link ? (
                                  <a
                                    href={hsBot.link}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-accent-main hover:underline"
                                  >
                                    {hsBot.link}
                                  </a>
                                ) : (
                                  <span className="text-red-500">Missing link</span>
                                )}
                            </div>
                          </>
                        ) : (
                          <span className="text-gray-400">Not set</span>
                        )}
                      </div>
                    </div>

                    {/* Sync Status */}
                    {syncAnalysis && (
                      <div className="pt-4 border-t">
                        <div className="font-medium mb-1 flex items-center justify-between">
                          <span>Sync Status:</span>
                          <button
                            onClick={handleRefreshHubSpotData}
                            disabled={isFetching}
                            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                            title="Refresh HubSpot data"
                          >
                            <IconWrapper icon={RefreshIcon} size={IconSize.sm} />
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className={
                            syncAnalysis.issues.length > 0 && syncAnalysis.overall_status === 'synced'
                              ? 'text-orange-600'
                              : syncAnalysis.issues.length > 0
                              ? 'text-orange-600'
                              : syncAnalysis.overall_status === 'synced'
                              ? 'text-green-600'
                              : syncAnalysis.overall_status === 'out_of_sync'
                              ? 'text-orange-600'
                              : 'text-red-600'
                          }>
                            {syncAnalysis.issues.length > 0 && syncAnalysis.overall_status === 'synced' ? (
                              `✓ Synced (${syncAnalysis.issues.length} incomplete)`
                            ) : syncAnalysis.issues.length > 0 ? (
                              `⚠ Incomplete (${syncAnalysis.issues.length} bot${syncAnalysis.issues.length > 1 ? 's' : ''})`
                            ) : syncAnalysis.overall_status === 'synced' ? (
                              '✓ In sync'
                            ) : syncAnalysis.overall_status === 'out_of_sync' ? (
                              '⚠ Out of sync'
                            ) : (
                              '❌ Error fetching from HubSpot'
                            )}
                          </span>
                          <Button
                            variant={ButtonVariant.outline}
                            onClick={handleSyncBotInfo}
                            disabled={isSyncing || !syncAnalysis.can_sync}
                            className="ml-2"
                          >
                            {isSyncing ? 'Syncing...' : 'Resync'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                );
              } else {
                // Multi Bot Comparison View
                // Get all unique slot numbers
                const allSlots = Array.from(
                  new Set([...dbBots.map(b => b.slot), ...hsBots.map(b => b.slot)])
                ).sort((a, b) => a - b);

                const getDbBot = (slot: number) => dbBots.find(b => b.slot === slot);
                const getHsBot = (slot: number) => hsBots.find(b => b.slot === slot);

                const getRowStyle = (slot: number) => {
                  const dbBot = getDbBot(slot);
                  const hsBot = getHsBot(slot);

                  if (!dbBot && hsBot && hsBot.name && hsBot.link) {
                    // New in HubSpot - blue background
                    return 'bg-blue-50 text-blue-900';
                  }
                  if (dbBot && !hsBot) {
                    // Will be removed - red/gray background
                    return 'bg-red-50 text-red-900';
                  }
                  if (!dbBot && hsBot && (!hsBot.name || !hsBot.link)) {
                    // Incomplete in HubSpot - gray background
                    return 'bg-gray-100 text-gray-600';
                  }
                  if (dbBot && hsBot) {
                    if (dbBot.name === hsBot.name && dbBot.link === hsBot.link) {
                      // In sync - green background
                      return 'bg-green-50 text-green-900';
                    } else {
                      // Out of sync - orange background
                      return 'bg-orange-50 text-orange-900';
                    }
                  }
                  return 'bg-gray-50 text-gray-600';
                };

                return (
                  <>
                    {/* Compact Comparison Table */}
                    <div className="mb-4">
                      <div className="text-xs font-medium text-gray-500 mb-2 flex">
                        <div className="w-8">Slot</div>
                        <div className="flex-1">Database</div>
                        <div className="flex-1">HubSpot</div>
                      </div>
                      <div className="space-y-1">
                        {allSlots.map(slot => {
                          const dbBot = getDbBot(slot);
                          const hsBot = getHsBot(slot);
                          const rowStyle = getRowStyle(slot);

                          return (
                            <div key={slot} className={`flex text-xs p-2 rounded ${rowStyle}`}>
                              <div className="w-8 font-medium">{slot}</div>
                              <div className="flex-1 break-words">
                                {dbBot ? (
                                  <a
                                    href={dbBot.link}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="hover:underline font-medium"
                                  >
                                    {dbBot.name}
                                  </a>
                                ) : null}
                              </div>
                              <div className="flex-1 break-words">
                                {hsBot?.link ? (
                                  <a
                                    href={hsBot.link}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="hover:underline font-medium"
                                  >
                                    {hsBot.name || '(Missing Name)'}
                                  </a>
                                ) : hsBot?.name ? (
                                  <span className="font-medium">{hsBot.name}</span>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Sync Status */}
                    {syncAnalysis && (
                      <div className="pt-4 border-t">
                        <div className="font-medium mb-1 flex items-center justify-between">
                          <span>Overall:</span>
                          <button
                            onClick={handleRefreshHubSpotData}
                            disabled={isFetching}
                            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                            title="Refresh HubSpot data"
                          >
                            <IconWrapper icon={RefreshIcon} size={IconSize.sm} />
                          </button>
                        </div>
                        <div className="mb-3">
                          <span className={
                            syncAnalysis.issues.length > 0 && syncAnalysis.overall_status === 'synced'
                              ? 'text-orange-600'
                              : syncAnalysis.issues.length > 0
                              ? 'text-orange-600'
                              : syncAnalysis.overall_status === 'synced'
                              ? 'text-green-600'
                              : syncAnalysis.overall_status === 'out_of_sync'
                              ? 'text-orange-600'
                              : 'text-red-600'
                          }>
                            {syncAnalysis.issues.length > 0 && syncAnalysis.overall_status === 'synced' ? (
                              `✓ Synced (${syncAnalysis.issues.length} incomplete)`
                            ) : syncAnalysis.issues.length > 0 ? (
                              `⚠ Incomplete (${syncAnalysis.issues.length} bot${syncAnalysis.issues.length > 1 ? 's' : ''})`
                            ) : syncAnalysis.overall_status === 'synced' ? (
                              '✓ In sync'
                            ) : syncAnalysis.overall_status === 'out_of_sync' ? (
                              '⚠ Out of sync'
                            ) : (
                              '❌ Error fetching from HubSpot'
                            )}
                          </span>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant={ButtonVariant.outline}
                            onClick={() => setBotDetailsModalOpen(true)}
                            className="flex-1"
                          >
                            View Details
                          </Button>
                          <Button
                            variant={ButtonVariant.outline}
                            onClick={handleSyncBotInfo}
                            disabled={isSyncing || !syncAnalysis.can_sync}
                            className="flex-1"
                          >
                            {isSyncing ? 'Syncing...' : 'Resync'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                );
              }
            })()}

          </div>
        </div>
      )}
      <EditSubscriptionModal
        user={localUser || user}
        open={open}
        handleClose={() => setOpen(false)}
      />
      <BotDetailsModal
        open={botDetailsModalOpen}
        handleClose={() => setBotDetailsModalOpen(false)}
        botInfo={(localUser || user)?.botInfo?.bots || []}
        hubspotBots={(localUser || user)?.hubspot?.bot_info?.bots || []}
        syncAnalysis={(localUser || user)?.sync_analysis}
        dealId={(localUser || user)?.hubspot?.deal_id}
        onResync={handleSyncBotInfo}
        isSyncing={isSyncing}
      />
    </div>
  );
};

export default UserEdit;
